import { useState, useMemo } from 'react'
import { Card, Col, Row, Badge, ProgressBar, Button, Form, Spinner, Table, Alert } from 'react-bootstrap'
import { useQuery } from '@apollo/client'
import { GET_DIFY_USAGE_REPORT } from '../../gql/queries/difyQueries'
import { useGetUser } from '../../store/userStore'
import { parseId } from '../../helpers'
import axios from 'axios'
import { toast } from 'react-toastify'
import { defineMessages, useIntl } from 'react-intl'

const M = defineMessages({
  pageTitle: { id: 'dashboard.dify.page.title', defaultMessage: 'Dify Usage' },
  pageSubtitle: {
    id: 'dashboard.dify.page.subtitle',
    defaultMessage: 'Audit and analytics of Dify AI usage',
  },
  lastDays: { id: 'dashboard.dify.filter.lastDays', defaultMessage: 'Last {days} days' },
  exportCsv: { id: 'dashboard.dify.action.exportCsv', defaultMessage: 'Export CSV' },
  exportSuccess: {
    id: 'dashboard.dify.toast.exportSuccess',
    defaultMessage: 'CSV exported successfully',
  },
  exportError: { id: 'dashboard.dify.toast.exportError', defaultMessage: 'Error exporting CSV' },
  loadError: {
    id: 'dashboard.dify.error.loadReports',
    defaultMessage: 'Error loading reports: {message}',
  },
  totalMessages: { id: 'dashboard.dify.card.totalMessages', defaultMessage: 'Total messages' },
  successfulCalls: {
    id: 'dashboard.dify.card.successfulCalls',
    defaultMessage: '{count} successful ({rate}%)',
  },
  tokensConsumed: { id: 'dashboard.dify.card.tokensConsumed', defaultMessage: 'Tokens consumed' },
  inputTokens: { id: 'dashboard.dify.card.inputTokens', defaultMessage: 'Input: {count}' },
  outputTokens: { id: 'dashboard.dify.card.outputTokens', defaultMessage: 'Output: {count}' },
  avgLatency: { id: 'dashboard.dify.card.avgLatency', defaultMessage: 'Average latency' },
  errorCalls: { id: 'dashboard.dify.card.errorCalls', defaultMessage: 'Errors: {count}' },
  timeoutCalls: { id: 'dashboard.dify.card.timeoutCalls', defaultMessage: 'Timeouts: {count}' },
  avgPerMessage: { id: 'dashboard.dify.card.avgPerMessage', defaultMessage: 'Average per message' },
  tokensPerMessage: {
    id: 'dashboard.dify.card.tokensPerMessage',
    defaultMessage: 'tokens per message',
  },
  periodBadge: { id: 'dashboard.dify.card.periodBadge', defaultMessage: 'Period: {days} days' },
  usageByAgent: { id: 'dashboard.dify.agent.title', defaultMessage: 'Usage by agent' },
  agentFallback: { id: 'dashboard.dify.agent.fallbackName', defaultMessage: 'Agent {id}' },
  agentCalls: { id: 'dashboard.dify.agent.calls', defaultMessage: '{count} msgs' },
  agentDetail: {
    id: 'dashboard.dify.agent.detail',
    defaultMessage: '{tokens} tokens | {latency}ms average',
  },
  statusDistribution: {
    id: 'dashboard.dify.status.title',
    defaultMessage: 'Distribution by status',
  },
  statusSuccess: { id: 'dashboard.dify.status.success', defaultMessage: 'Success' },
  statusError: { id: 'dashboard.dify.status.error', defaultMessage: 'Error' },
  statusTimeout: { id: 'dashboard.dify.status.timeout', defaultMessage: 'Timeout' },
  statusCount: { id: 'dashboard.dify.status.count', defaultMessage: '{count} ({percent}%)' },
  recentLogs: { id: 'dashboard.dify.logs.title', defaultMessage: 'Latest processed messages' },
  colDateTime: { id: 'dashboard.dify.logs.col.dateTime', defaultMessage: 'Date/Time' },
  colStatus: { id: 'common.field.status', defaultMessage: 'Status' },
  colInput: { id: 'dashboard.dify.logs.col.input', defaultMessage: 'Input' },
  colOutput: { id: 'dashboard.dify.logs.col.output', defaultMessage: 'Output' },
  colTotal: { id: 'common.field.total', defaultMessage: 'Total' },
  colLatency: { id: 'dashboard.dify.logs.col.latency', defaultMessage: 'Latency' },
  noRecords: { id: 'dashboard.dify.logs.empty', defaultMessage: 'No records available' },
})

// Decorative icons — not translatable content (guía §17.3)
const ICON_CHART = '📊'
const ICON_DOWNLOAD = '📥'
const ICON_UPLOAD = '📤'
const ICON_TOKENS = '💾'
const ICON_LATENCY = '⏱️'
const ICON_TREND = '📈'
const ICON_AGENTS = '👥'
const ICON_LOGS = '📋'
const ICON_SUCCESS = '✅'
const ICON_ERROR = '❌'
const ICON_TIMEOUT = '⏳'

const EMPTY_VALUE = '-'
const ZERO = '0'
const formatMs = (value: number | string) => `${value}ms`
/** Prefixes a decorative icon to an already-translated label (guía §17.3). */
const withIcon = (icon: string, label: string) => `${icon} ${label}`

export const DifyUsageReportPage = () => {
  const intl = useIntl()
  const [days, setDays] = useState(30)
  const user = useGetUser((s) => s.user)
  const organizationId = user?.user?.organization?.id
  const decodedOrgId = organizationId ? String(parseId(organizationId)) : ''

  const { data, loading, error, refetch } = useQuery(GET_DIFY_USAGE_REPORT, {
    variables: { days, organizationId: decodedOrgId },
    skip: !decodedOrgId,
  })

  const report = data?.difyUsageReport || {}

  const statusLabels: Record<string, string> = {
    success: `${ICON_SUCCESS} ${intl.formatMessage(M.statusSuccess)}`,
    error: `${ICON_ERROR} ${intl.formatMessage(M.statusError)}`,
    timeout: `${ICON_TIMEOUT} ${intl.formatMessage(M.statusTimeout)}`,
  }

  const badgeStatusLabels: Record<string, string> = {
    success: intl.formatMessage(M.statusSuccess),
    error: intl.formatMessage(M.statusError),
    timeout: intl.formatMessage(M.statusTimeout),
  }

  const handleExportCSV = async () => {
    try {
      const response = await axios.get('/api/chat/dify-usage-report/export/', {
        params: { days, org_id: decodedOrgId },
        responseType: 'blob',
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `dify-usage-${new Date().toISOString().split('T')[0]}.csv`)
      document.body.appendChild(link)
      link.click()
      link.parentElement?.removeChild(link)
      toast.success(intl.formatMessage(M.exportSuccess))
    } catch (err) {
      toast.error(intl.formatMessage(M.exportError))
    }
  }

  if (loading) {
    return (
      <div className="container-fluid pt-5 text-center">
        <Spinner animation="border" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="container-fluid pt-5">
        <Alert variant="danger">
          {intl.formatMessage(M.loadError, { message: error.message })}
        </Alert>
      </div>
    )
  }

  const successRate = report.success_rate || 0

  return (
    <div className="container-fluid pt-5 pb-5">
      {/* Header */}
      <div className="mb-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div>
          <h2 className="mb-1">
            {withIcon(ICON_CHART, intl.formatMessage(M.pageTitle))}
          </h2>
          <small className="text-muted">{intl.formatMessage(M.pageSubtitle)}</small>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Form.Select
            value={days}
            onChange={(e) => {
              setDays(Number(e.target.value))
              refetch({ days: Number(e.target.value), organizationId: decodedOrgId })
            }}
            style={{ width: '200px' }}
          >
            <option value={7}>{intl.formatMessage(M.lastDays, { days: 7 })}</option>
            <option value={30}>{intl.formatMessage(M.lastDays, { days: 30 })}</option>
            <option value={90}>{intl.formatMessage(M.lastDays, { days: 90 })}</option>
          </Form.Select>
          <Button variant="outline-primary" onClick={handleExportCSV}>
            {withIcon(ICON_DOWNLOAD, intl.formatMessage(M.exportCsv))}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <Row className="mb-4 g-3">
        <Col md={3}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-2">
                    {withIcon(ICON_CHART, intl.formatMessage(M.totalMessages))}
                  </h6>
                  <h2 className="text-primary mb-2">
                    {report.total_calls?.toLocaleString() || ZERO}
                  </h2>
                  <ProgressBar
                    now={successRate}
                    variant={successRate >= 95 ? 'success' : successRate >= 80 ? 'warning' : 'danger'}
                  />
                  <small className="text-muted d-block mt-2">
                    {intl.formatMessage(M.successfulCalls, {
                      count: report.successful_calls || '0',
                      rate: successRate,
                    })}
                  </small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div>
                <h6 className="text-muted mb-2">
                  {withIcon(ICON_TOKENS, intl.formatMessage(M.tokensConsumed))}
                </h6>
                <h2 className="text-info mb-2">
                  {report.total_tokens?.toLocaleString() || ZERO}
                </h2>
                <small className="text-muted d-block">
                  {withIcon(ICON_DOWNLOAD, intl.formatMessage(M.inputTokens, {
                    count: report.input_tokens?.toLocaleString() || '0',
                  }))}
                </small>
                <small className="text-muted d-block">
                  {withIcon(ICON_UPLOAD, intl.formatMessage(M.outputTokens, {
                    count: report.output_tokens?.toLocaleString() || '0',
                  }))}
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div>
                <h6 className="text-muted mb-2">
                  {withIcon(ICON_LATENCY, intl.formatMessage(M.avgLatency))}
                </h6>
                <h2 className="text-warning mb-2">
                  {formatMs(report.avg_latency_ms?.toFixed(0) || '0')}
                </h2>
                <small className="text-muted d-block">
                  {withIcon(ICON_ERROR, intl.formatMessage(M.errorCalls, {
                    count: report.error_calls || '0',
                  }))}
                </small>
                <small className="text-muted d-block">
                  {withIcon(ICON_TIMEOUT, intl.formatMessage(M.timeoutCalls, {
                    count: report.timeout_calls || '0',
                  }))}
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div>
                <h6 className="text-muted mb-2">
                  {withIcon(ICON_TREND, intl.formatMessage(M.avgPerMessage))}
                </h6>
                <h2 className="text-success mb-2">
                  {report.total_calls > 0
                    ? ((report.total_tokens || 0) / (report.total_calls || 1)).toFixed(1)
                    : ZERO}
                </h2>
                <small className="text-muted d-block">
                  {intl.formatMessage(M.tokensPerMessage)}
                </small>
                <div className="mt-3">
                  <Badge bg="info" className="me-1">
                    {intl.formatMessage(M.periodBadge, { days })}
                  </Badge>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* By Agent */}
      {report.by_agent && report.by_agent.length > 0 && (
        <Row className="mb-4">
          <Col lg={6}>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-light">
                <h5 className="mb-0">
                  {withIcon(ICON_AGENTS, intl.formatMessage(M.usageByAgent))}
                </h5>
              </Card.Header>
              <Card.Body>
                {report.by_agent.map((agent: any) => (
                  <div key={agent.agent_id} className="mb-4">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="fw-bold">
                        {agent.agent_name ||
                          intl.formatMessage(M.agentFallback, { id: agent.agent_id })}
                      </span>
                      <Badge bg="info">
                        {intl.formatMessage(M.agentCalls, { count: agent.calls })}
                      </Badge>
                    </div>
                    <ProgressBar
                      now={(agent.calls / (report.total_calls || 1)) * 100}
                      variant="info"
                      className="mb-2"
                    />
                    <small className="text-muted d-block">
                      {intl.formatMessage(M.agentDetail, {
                        tokens: agent.tokens?.toLocaleString() || 0,
                        latency: agent.avg_latency_ms,
                      })}
                    </small>
                  </div>
                ))}
              </Card.Body>
            </Card>
          </Col>

          <Col lg={6}>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-light">
                <h5 className="mb-0">
                  {withIcon(ICON_CHART, intl.formatMessage(M.statusDistribution))}
                </h5>
              </Card.Header>
              <Card.Body>
                <div>
                  {report.by_status &&
                    Object.entries(report.by_status).map(([status, data]: any) => (
                      <div key={status} className="mb-3">
                        <div className="d-flex justify-content-between mb-2">
                          <span>{statusLabels[status] ?? status}</span>
                          <Badge
                            bg={
                              status === 'success'
                                ? 'success'
                                : status === 'error'
                                  ? 'danger'
                                  : 'warning'
                            }
                          >
                            {intl.formatMessage(M.statusCount, {
                              count: data.count,
                              percent: ((data.count / (report.total_calls || 1)) * 100).toFixed(1),
                            })}
                          </Badge>
                        </div>
                        <ProgressBar
                          now={(data.count / (report.total_calls || 1)) * 100}
                          variant={
                            status === 'success'
                              ? 'success'
                              : status === 'error'
                                ? 'danger'
                                : 'warning'
                          }
                        />
                      </div>
                    ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Recent Logs Table */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-light">
          <h5 className="mb-0">
            {withIcon(ICON_LOGS, intl.formatMessage(M.recentLogs))}
          </h5>
        </Card.Header>
        <Card.Body className="p-0">
          <Table hover className="mb-0">
            <thead>
              <tr className="bg-light">
                <th>{intl.formatMessage(M.colDateTime)}</th>
                <th>{intl.formatMessage(M.colStatus)}</th>
                <th>{intl.formatMessage(M.colInput)}</th>
                <th>{intl.formatMessage(M.colOutput)}</th>
                <th>{intl.formatMessage(M.colTotal)}</th>
                <th>{intl.formatMessage(M.colLatency)}</th>
              </tr>
            </thead>
            <tbody>
              {report.recent_logs && report.recent_logs.length > 0 ? (
                report.recent_logs.map((log: any) => (
                  <tr key={log.id}>
                    <td>
                      <small>
                        {new Date(log.created).toLocaleString()}
                      </small>
                    </td>
                    <td>
                      <Badge
                        bg={
                          log.status === 'success'
                            ? 'success'
                            : log.status === 'error'
                              ? 'danger'
                              : 'warning'
                        }
                      >
                        {badgeStatusLabels[log.status] ?? log.status}
                      </Badge>
                    </td>
                    <td>
                      <small>{log.input_tokens || EMPTY_VALUE}</small>
                    </td>
                    <td>
                      <small>{log.output_tokens || EMPTY_VALUE}</small>
                    </td>
                    <td>
                      <small className="fw-bold">{log.total_tokens || EMPTY_VALUE}</small>
                    </td>
                    <td>
                      <small>{formatMs(log.dify_latency_ms)}</small>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    {intl.formatMessage(M.noRecords)}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </div>
  )
}
